import { useEffect, useState } from 'react';

import { useFormikContext } from 'formik';
import compact from 'lodash/compact';
import uniq from 'lodash/uniq';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';

import { makeSelectAuthSystemFileUploadLimits } from '@/main/pages/PageAuth/selectors/authSelectors';
import { useSubscriberIdContext } from '@/main/pages/PageSubscriberId/context';
import { stringFormatParams } from '@/main/utils/stringFormat';
import { icons } from '@assets/index';
import { Button, LinkBlock } from '@components/common';
import { ImgStyled } from '@components/common/Block/Img';
import { ELinkTarget } from '@components/common/ExternalLink/ExternalLink';
import { FormLabelStyled } from '@components/common/FormStyled/FormStyled';
import { LabelMarkStyled } from '@components/common/Input/styled/InputStyled';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import FormField from '@modules/form/components/FormField';
import { ELocaleKeys } from '@modules/internationalization/intConstants';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import ModalErrorCommon from '@modules/modal/components/ModalError';
import { modalOpen } from '@modules/modal/modalActions';
import { EModalStatus, IModalRenderProps } from '@modules/modal/types/modalTypes';

import {
    getFileIcon,
    isPersonalDataNewFile,
    SubscriberIdPersonalDataFileExtensions,
    validateFile,
    getFileDownloadUrl,
} from '../../const/SubscriberIdPersonalDataEdit';
import {
    personalDataMandatoryAttributesSelector,
    personalDataMaxFileAmountSelector,
    personalDataMaxFileSizeSelector,
    personalDataPassportIdSelector,
    personalDataTranslationSelector,
} from '../../selectors';
import {
    useGetPersonalDataFileQuery,
    useRemovePersonalDataNewFileMutation,
    useUploadPersonalDataFileMutation,
} from '../../store';
import { IPersonalDataFile, IPersonalDataNewFile } from '../../types/file';
import { IPersonalDataInfoEdit } from '../../types/info-edit';
import { IPersonalDataDocType, PersonalDataType as T } from '../../types/meta';
import {
    PersonalDataEditFileRemoveIconStyled,
    PersonalDataEditFileStyled,
} from '../styled/SubscriberInfoDataInfoStyled';

interface IPersonalDataEditFile {
    docTypeSelected?: IPersonalDataDocType;
    excludeFiles: string[];
    setExcludeFiles: (files: string[]) => void;
}

export const PersonalDataEditFile = ({ docTypeSelected, excludeFiles, setExcludeFiles }: IPersonalDataEditFile) => {
    const dispatch = useDispatch();

    const { form: tForm, modal: tModal } = useSelector(makeSelectTranslationCommon());
    const { files: tFiles, personalDataEdit: tPersonalDataEdit } = useSelector(personalDataTranslationSelector());

    const { subscriberId } = useSubscriberIdContext();
    const { setFieldValue, getFieldProps } = useFormikContext<IPersonalDataInfoEdit>();

    const passportId = useSelector(personalDataPassportIdSelector(subscriberId));
    const maxFileAmount = useSelector(personalDataMaxFileAmountSelector(subscriberId));
    const maxFileSize = useSelector(personalDataMaxFileSizeSelector(subscriberId));
    const { maxFileNameLength } = useSelector(makeSelectAuthSystemFileUploadLimits());
    const mandatoryAttributes = useSelector(personalDataMandatoryAttributesSelector(subscriberId));

    const [error, setError] = useState<string>('');

    const { alreadyUploadedFiles } = useGetPersonalDataFileQuery(subscriberId, {
        selectFromResult: ({ data }) => ({
            alreadyUploadedFiles: data?.filter((file) => !excludeFiles.includes(file.id)) ?? [],
        }),
    });

    const [
        uploadFile,
        {
            data: uploadedFile,
            isLoading: isUploadingNewFile,
            isError: isUploadedNewFileError,
            isSuccess: isUploadedNewFileSuccess,
        },
    ] = useUploadPersonalDataFileMutation();

    const [
        removeFile,
        {
            originalArgs: removingNewFileId,
            isLoading: isRemovingNewFile,
            isSuccess: isRemovedNewFileSuccess,
            isError: isRemovedNewFileError,
        },
    ] = useRemovePersonalDataNewFileMutation();

    const newUploadedFiles = (getFieldProps<IPersonalDataNewFile[]>(T.files).value || []).filter(
        (file: IPersonalDataFile | IPersonalDataNewFile) => (isPersonalDataNewFile(file) ? !!file.fileId : false),
    );

    useEffect(() => {
        if (isUploadedNewFileSuccess && uploadedFile) {
            setFieldValue(T.files, [...getFieldProps(T.files).value, uploadedFile]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadedFile, isUploadedNewFileSuccess]);

    useEffect(() => {
        if (isUploadedNewFileError || isRemovedNewFileError) {
            const errorMessage = isUploadedNewFileError
                ? tPersonalDataEdit.modal[EModalStatus.failure].upload
                : tPersonalDataEdit.modal[EModalStatus.failure].remove;

            dispatch(
                modalOpen({
                    render: (p: IModalRenderProps) => (
                        <ModalErrorCommon translation={tModal} onCloseModal={p.onCloseModal}>
                            {errorMessage}
                        </ModalErrorCommon>
                    ),
                }),
            );
        }
    }, [dispatch, isRemovedNewFileError, isUploadedNewFileError, tModal, tPersonalDataEdit.modal]);

    useEffect(() => {
        if (isRemovedNewFileSuccess) {
            setFieldValue(
                T.files,
                newUploadedFiles.filter((f) => f.fileId !== removingNewFileId),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRemovedNewFileSuccess, removingNewFileId]);

    const onDropHandler = (acceptedFiles: File[]) => {
        const validationErrors = compact(
            acceptedFiles.map((file) =>
                validateFile(file, alreadyUploadedFiles, newUploadedFiles, tForm, maxFileSize, maxFileNameLength),
            ),
        );
        // shor first validation error
        if (validationErrors[0]) {
            setError(validationErrors[0]);
        } else {
            setError('');
            uploadFile({ file: acceptedFiles[0], subscriberId });
        }
    };

    const removeNewFile = (file: IPersonalDataNewFile) => {
        removeFile(file.fileId);
    };

    const removeAlreadyUploadedFile = (file: IPersonalDataFile) => {
        if (alreadyUploadedFiles.length === 1) {
            const [alreadyUploadedFile] = alreadyUploadedFiles;
            if (alreadyUploadedFile.id === file.id) {
                setFieldValue(T.files, []);
            }
        }
        setExcludeFiles(uniq([...excludeFiles, file.id]));
    };

    const { getInputProps, open } = useDropzone({
        onDrop: onDropHandler,
        multiple: false,
        noClick: false,
        noKeyboard: false,
    });

    const isFileRequired = mandatoryAttributes.includes(T.files) && passportId !== docTypeSelected?.id;

    const alreadyUploadedFile = alreadyUploadedFiles.map((file) => (
        <div className="edit-form-file__item" key={file.id}>
            <LinkBlock isExternalUrl target={ELinkTarget.BLANK} url={getFileDownloadUrl(file, subscriberId)}>
                <div className="edit-form-file__name">
                    <ImgStyled className="medium edit-form-file__extension" src={getFileIcon(file)} />
                    {file.name}
                </div>
            </LinkBlock>
            <PersonalDataEditFileRemoveIconStyled onClick={() => removeAlreadyUploadedFile(file)} />
        </div>
    ));

    const newUploadedFile = newUploadedFiles.map(({ fileId, file }) => (
        <div className="edit-form-file__item" key={fileId}>
            <div className="edit-form-file__name">
                <ImgStyled className="medium edit-form-file__extension" src={getFileIcon(file)} />
                {file.name}
            </div>
            <PersonalDataEditFileRemoveIconStyled onClick={() => removeNewFile({ fileId, file })} />
        </div>
    ));

    return (
        <FormField hideValidation dataName="edit-file" name={T.files}>
            {() => (
                <>
                    <FormLabelStyled>
                        {tFiles.fields.name}
                        {isFileRequired && <LabelMarkStyled>*</LabelMarkStyled>}
                        <ArrowTooltip
                            message={stringFormatParams(tPersonalDataEdit.file.formats, {
                                [ELocaleKeys.format]: SubscriberIdPersonalDataFileExtensions.join(', '),
                            })}
                            title={stringFormatParams(tPersonalDataEdit.file.maxSize, {
                                [ELocaleKeys.number]: maxFileAmount,
                            })}
                        >
                            <ImgStyled className="small" src={icons.infoBlueIcon} />
                        </ArrowTooltip>
                    </FormLabelStyled>
                    <PersonalDataEditFileStyled>
                        {/* Hidden input */}
                        <input {...getInputProps()} />
                        <Button
                            className="edit-form-file__btn"
                            disabled={
                                (alreadyUploadedFiles?.length ?? 0) + newUploadedFiles.length >= maxFileAmount ||
                                isUploadingNewFile ||
                                isRemovingNewFile
                            }
                            onClick={open}
                        >
                            {tForm.attachFile}
                        </Button>
                        {!!error && <div className="edit-form-file__error">{error}</div>}
                        <div className="edit-form-file__list">
                            {alreadyUploadedFile}
                            {newUploadedFile}
                            {(isUploadingNewFile || isRemovingNewFile) && (
                                <ImgStyled className="medium" src={icons.searchIndicator} style={{ width: 32 }} />
                            )}
                        </div>
                    </PersonalDataEditFileStyled>
                </>
            )}
        </FormField>
    );
};
